import type { FC, MouseEvent } from 'react';
import React from 'react';
import { useNamedContainers } from '@alfa-bank/site-named-container/utils';
import type { TFocusEventHandler } from '@alfa-bank/site-pure-input/src/models';

import { getInputType, getHandler } from './helpers';
import type { TInput } from './models/models';

export const Input: FC<TInput> = ({
    CoreInput,
    Hint,
    addHiddenInput,
    autoFocus,
    block = true,
    caption,
    children,
    className = '',
    clear = false,
    dataTestId = 'input',
    disabled,
    error,
    forwardedRef,
    hideStatusIcon,
    id,
    infoBlock,
    infoText,
    labelView,
    name = '',
    onBlur,
    onCancel,
    onChange,
    onClick,
    onFocus,
    onKeyDown,
    onMouseDown,
    onMouseUp,
    onPaste,
    placeholder: label,
    placeholderText,
    size = 'm',
    styles,
    success,
    type,
    valid,
    value = '',
    ...rest
}) => {
    /** CoreInput поддерживает аддоны, у нас используем слоты
     *  Посмотреть тут https://core-ds.github.io/core-components/master/?path=/story/input--input
     */
    const { bottomAddon, leftAddon, rightAddon } = useNamedContainers(children);

    /** Скрытый input */
    const HiddenInput = addHiddenInput && disabled && (
        <input name={name} readOnly type="hidden" value={value} />
    );

    /** Если есть ошибка, для текста ошибки используем ее
     *  иначе hint будет на месте текста для ошибки
     */
    const hintText = error ? '' : caption;

    /** У коров для ошибки можно передать как строку как и флаг
     *  у нас есть отдельный пропс caption для текста ошибки
     */
    const errorContent = (error && caption && !infoBlock ? caption : '') || error;

    const onFocusHandler: TFocusEventHandler = (...e) => {
        if (onFocus) {
            onFocus(...e);
        }
    };

    const onBlurHandler: TFocusEventHandler = (...e) => {
        if (onBlur) {
            onBlur(...e);
        }
    };

    return (
        /** Т.к в CoreInput нет type hidden, скрываем всю разметку */
        <div className={styles.inputWrapper} hidden={type === 'hidden'}>
            <CoreInput
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={autoFocus}
                block={block}
                bottomAddons={bottomAddon}
                className={className}
                clear={clear}
                dataTestId={dataTestId}
                disabled={disabled}
                error={errorContent}
                hint={hintText}
                id={id}
                label={label}
                labelView={labelView}
                leftAddons={leftAddon}
                name={name}
                onBlur={getHandler(onBlurHandler)}
                onChange={getHandler(onChange)}
                onClear={onCancel}
                onClick={onClick as (event: MouseEvent<HTMLDivElement>) => void}
                onFocus={getHandler(onFocusHandler)}
                onKeyDown={getHandler(onKeyDown)}
                onMouseDown={
                    /** Костыль т.к в CoreInput HTMLDivElement а не HTMLInputElement*/
                    getHandler(onMouseDown) as (event: MouseEvent<HTMLDivElement>) => void
                }
                onMouseUp={
                    /** Костыль т.к в CoreInput HTMLDivElement а не HTMLInputElement*/
                    getHandler(onMouseUp) as (event: MouseEvent<HTMLDivElement>) => void
                }
                onPaste={getHandler(onPaste)}
                placeholder={placeholderText}
                rightAddons={rightAddon || (infoBlock && infoText && <Hint>{infoText}</Hint>)}
                size={size}
                /** Если иконка скрыта не показываем ее
                 *  valid оставленно для обратной совместимости
                 */
                success={Boolean(!hideStatusIcon && (success || valid))}
                type={getInputType(type)}
                value={String(value)}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...rest}
                ref={forwardedRef}
            />
            {HiddenInput}
        </div>
    );
};
